import React, { useEffect, useMemo, useState } from 'react';
import { procurementService, LinhaDaPreviaDoTitular, PreviaDosTitulares } from '../../services/api';
import CascaDeModal from '../shared/CascaDeModal';
import { EstadoDaLista, useEstadoDaCarga } from '../../design';
import { TONE_BADGE, TONE_TEXT } from '../../utils/statusColors';

/**
 * PREENCHER OS TITULARES (T1, Heitor 23/10/2026; backend #532). O titular dos acessos vivia dentro do nome, entre
 * parênteses. O backend propõe, a partir dele, e o Heitor confere antes de gravar.
 *
 * ⛔ Nem todo parêntese é titular: há FASE ("Monitorar", "Encontrar", "Disputar") e FUNÇÃO ("Protocolo"). O backend marca
 * essas linhas como `suspeito` e diz POR QUÊ em `motivo` — a tela mostra o motivo por extenso, e a linha suspeita vem
 * DESMARCADA. Quatro falsas no meio de 46 se aprovam sem ver; é para isso que o texto está aqui.
 * `titularProposto: null` = não aplicar: a caixa fica desabilitada (o apply recusaria).
 * O apply recebe só os ids: grava o que a prévia mostrou, nunca um titular digitado.
 */
const naoMuda = (l: LinhaDaPreviaDoTitular) => l.titularProposto === null || (l.titularProposto === l.titularAtual && l.nomeSemTitular === l.nomeAtual);

export default function PreencherTitulares({ onFechar, onAplicado }: { onFechar: () => void; onAplicado: () => void }) {
  const { rodar, ...carga } = useEstadoDaCarga();
  const [previa, setPrevia] = useState<PreviaDosTitulares | null>(null);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState<{ aplicados: number; recusados: Array<{ id: number; motivo: string }> } | null>(null);

  const ler = () => rodar(async () => {
    const p = await procurementService.previaDosTitulares();
    setPrevia(p);
    // Já marcadas: só as prováveis que mudam algo. As suspeitas começam desmarcadas, de propósito.
    setMarcados(new Set(p.linhas.filter((l) => l.confianca === 'provavel' && !naoMuda(l)).map((l) => l.id)));
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ler(); }, []);

  const linhas = useMemo(() => previa?.linhas || [], [previa]);
  const nomePorId = useMemo(() => new Map(linhas.map((l) => [l.id, l.nomeAtual])), [linhas]);
  const alternar = (id: number) => setMarcados((m) => { const n = new Set(m); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const aplicar = async () => {
    setAplicando(true);
    try {
      const r = await procurementService.aplicarTitulares(Array.from(marcados));
      setResultado(r);
      if (r.aplicados > 0) onAplicado();
    } catch (e: any) {
      setResultado({ aplicados: 0, recusados: [{ id: 0, motivo: e?.response?.data?.message || e?.message || 'o servidor não respondeu' }] });
    } finally { setAplicando(false); }
  };

  return (
    <CascaDeModal
      largura='lg'
      titulo='Preencher titulares'
      onClose={onFechar}
      corpoClassName='p-5 space-y-4'
      rodape={
        <div className='ml-auto flex gap-3'>
          <button onClick={onFechar} className='px-4 py-2 text-sm text-slate-300 hover:text-white'>{resultado ? 'Fechar' : 'Cancelar'}</button>
          {!resultado && (
            <button onClick={aplicar} disabled={aplicando || marcados.size === 0} data-aplicar
              className='px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white rounded-lg text-sm'>
              {aplicando ? 'Aplicando…' : `Aplicar ${marcados.size} ${marcados.size === 1 ? 'titular' : 'titulares'}`}
            </button>
          )}
        </div>
      }
    >
      <EstadoDaLista carga={carga} temDados={!!previa} oQue='a prévia dos titulares' onTentarDeNovo={ler} vazio={<p className='text-sm text-slate-400'>Nenhum acesso para preencher.</p>}>
        {resultado ? (
          <div className='space-y-2' data-resultado-titulares>
            <p className='text-sm text-central-texto'>{resultado.aplicados} {resultado.aplicados === 1 ? 'titular gravado' : 'titulares gravados'}.</p>
            {resultado.recusados.length > 0 && (
              <div className={`text-sm ${TONE_TEXT.warning}`}>
                <p className='font-medium'>Recusados pelo servidor:</p>
                <ul className='list-disc pl-5'>
                  {resultado.recusados.map((r) => <li key={r.id}>{nomePorId.get(r.id) || `#${r.id}`}: {r.motivo}</li>)}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <>
            <p className='text-sm text-slate-300' data-resumo-titulares>{previa?.resumo}</p>
            <p className='text-xs text-slate-500'>As linhas suspeitas vêm desmarcadas e dizem por quê. Marque só o que for de fato o titular da conta. O nome do portal perde o parêntese quando ele vira titular.</p>
            <div className='divide-y divide-central-borda border border-central-borda rounded-central'>
              {linhas.map((l) => {
                const bloqueada = l.titularProposto === null;
                const parada = naoMuda(l);
                return (
                  <label key={l.id} data-linha-titular={l.id} data-confianca={l.confianca}
                    className={`flex items-start gap-3 px-3 py-2.5 ${bloqueada ? 'opacity-60' : 'cursor-pointer hover:bg-central-elevado'} ${l.confianca === 'suspeito' ? 'border-l-[3px] border-l-amber-500' : ''}`}>
                    <input type='checkbox' className='mt-1' checked={marcados.has(l.id)} disabled={bloqueada} onChange={() => alternar(l.id)}
                      aria-label={`Aplicar o titular de ${l.nomeAtual}`} />
                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-2 text-sm'>
                        <span className='text-central-texto font-medium'>{l.nomeAtual}</span>
                        {!parada && <>
                          <span className='text-slate-500'>→</span>
                          <span className='text-central-texto'>{l.nomeSemTitular}</span>
                          <span className='text-slate-400'>· titular</span>
                          <span className='text-central-texto font-medium'>{l.titularProposto}</span>
                        </>}
                        {parada && <span className='text-slate-500 text-xs'>não muda</span>}
                        {l.confianca === 'suspeito' && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.warning}`}>suspeito</span>}
                      </div>
                      {l.motivo && <p className={`text-xs mt-0.5 ${l.confianca === 'suspeito' ? TONE_TEXT.warning : 'text-slate-500'}`} data-motivo-titular>{l.motivo}</p>}
                    </div>
                  </label>
                );
              })}
            </div>
          </>
        )}
      </EstadoDaLista>
    </CascaDeModal>
  );
}
